export type EntryReviewReason = 'sensitive' | 'manual' | 'mp_user' | null;

export type EntryReviewFields = {
  review_reason?: EntryReviewReason;
  matched_sensitive_words?: string[];
  draft_errmsg?: string | null;
};

export function formatSensitiveWordsInline(words: string[] | undefined): string {
  const list = (words ?? []).map((w) => w.trim()).filter(Boolean);
  if (list.length === 0) return '';
  return list.join('、');
}

/** 审核弹窗顶部：待审原因与敏感词命中（仅 awaiting_review 时展示） */
export function EntryReviewAlert({
  draftStatus,
  reviewReason,
  matchedWords,
}: {
  draftStatus: string;
  reviewReason?: EntryReviewReason;
  matchedWords?: string[];
}) {
  if (draftStatus !== 'awaiting_review') return null;

  const words = (matchedWords ?? []).map((w) => w.trim()).filter(Boolean);
  const isSensitive = reviewReason === 'sensitive' || words.length > 0;

  if (reviewReason === 'mp_user') {
    return (
      <div
        className="rounded-xl border border-violet-200/90 bg-violet-50/95 px-4 py-3.5 shadow-sm"
        role="status"
      >
        <div className="flex gap-3">
          <span
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-800"
            aria-hidden
          >
            <i className="fa-solid fa-mobile-screen text-sm" />
          </span>
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-sm font-semibold text-violet-950">待审核：小程序用户投稿</p>
            <p className="text-sm leading-relaxed text-violet-900/95">
              本条由 C 端用户自助发布。请核对标题、图片与描述；确认合规后点「通过」在小程序公开展示，无需写入公众号草稿箱。
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isSensitive) {
    return (
      <div
        className="rounded-xl border border-amber-200/90 bg-amber-50/95 px-4 py-3.5 shadow-sm"
        role="alert"
      >
        <div className="flex gap-3">
          <span
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800"
            aria-hidden
          >
            <i className="fa-solid fa-triangle-exclamation text-sm" />
          </span>
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-sm font-semibold text-amber-950">待审核：命中敏感词</p>
            <p className="text-sm leading-relaxed text-amber-900/95">
              请修改标题或正文并保存后，再点「通过」推送到公众号草稿箱。
            </p>
            {words.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium text-amber-900/80">命中词：</span>
                {words.map((w) => (
                  <span
                    key={w}
                    className="inline-flex max-w-full items-center rounded-lg border border-amber-300/80 bg-white px-2.5 py-1 font-mono text-xs font-medium text-amber-950 shadow-sm"
                  >
                    {w}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-amber-800/90">当前正文仍匹配站点敏感词规则，请编辑后重试。</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-sky-200/90 bg-sky-50/95 px-4 py-3.5 shadow-sm"
      role="status"
    >
      <div className="flex gap-3">
        <span
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-800"
          aria-hidden
        >
          <i className="fa-solid fa-user-check text-sm" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-sky-950">待人工审核</p>
          <p className="mt-1 text-sm leading-relaxed text-sky-900/95">
            站点已开启人工审核。请核对标题、正文与图片，确认无误后点「通过」写入公众号草稿箱。
          </p>
        </div>
      </div>
    </div>
  );
}

/** 详情「草稿说明」：敏感词待审已在顶部提示，避免重复长文案 */
export function entryDraftErrmsgForDisplay(entry: EntryReviewFields & { draft_status: string }): string | null {
  const msg = (entry.draft_errmsg ?? '').trim();
  if (!msg) return null;
  if (entry.draft_status === 'awaiting_review') {
    if (
      entry.review_reason === 'sensitive' ||
      entry.review_reason === 'manual' ||
      (entry.matched_sensitive_words?.length ?? 0) > 0
    ) {
      return null;
    }
  }
  return msg;
}
